import { useEffect, useRef, useState, type ReactNode } from "react";
import { sichererPfad } from "../lib/weiter";
import { api } from "../data";
import { currentLang, t as tGlobal } from "../lib/sprache";
import { googleClientId, gsiLaden, nonceErzeugen } from "./google-identity";
import { Dialog } from "./ui";

/**
 * „Mit Google fortfahren“ (Inhaber 02.10.2026): oben auf Anmelden, Testkonto anlegen und Einladung, darunter „oder“ und das
 * bisherige Formular. Erscheint nur, wenn Google in Supabase eingeschaltet ist (authProviders). vorher() prüft die Seite vor der
 * Weiterleitung (z. B. Zustimmung angekreuzt) und merkt sich, was nach der Rückkehr gebraucht wird; ein Text dort ist der Fehler.
 *
 * Fenster statt Fehlermeldung (Inhaber 13.10.2026): Fehlt beim Klick auf Google noch eine Zustimmung, öffnet sich ein Fenster
 * mit genau diesen Haken. Die Anmeldung bei Google bleibt gültig; nach „Weiter“ geht es ohne zweiten Klick auf Google weiter.
 */
/** Einmal je Seitenaufruf beim Anmeldedienst fragen, welche Anbieter eingeschaltet sind */
let providers: { google: boolean } | null = null;
let laden: Promise<{ google: boolean }> | null = null;
function providersLaden(): Promise<{ google: boolean }> {
  laden ??= api.authProviders!().then((p) => (providers = p)).catch(() => { laden = null; return { google: false }; });
  return laden;
}

export function GoogleAnmeldung({ redirectPath, vorher, fenster, t = tGlobal, nurUmleitung = false }: {
  redirectPath: string;
  vorher?: () => string | null;
  /** offen: fehlt noch etwas? inhalt: die Haken (teilen ihren Zustand mit der Seite); bereit: alles angehakt */
  fenster?: { offen: boolean; bereit: boolean; titel: string; inhalt: ReactNode };
  t?: (de: string, en: string) => string;
  /** Nur der eigene Knopf mit Weiterleitung zu Google, ohne Googles Popup-Knopf (Konsole als Dock-App: Popups kommen dort nicht zurück) */
  nurUmleitung?: boolean;
}) {
  const [google, setGoogle] = useState<boolean>(providers?.google ?? false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Googles eigener Knopf (Identity Services); scheitert das Laden, bleibt der eigene Knopf mit Umleitung
  const [gsi, setGsi] = useState<"aus" | "laedt" | "bereit">(!nurUmleitung && googleClientId() && api.signInWithGoogleIdToken ? "laedt" : "aus");
  const knopf = useRef<HTMLDivElement>(null);
  const vorherRef = useRef(vorher);
  vorherRef.current = vorher;
  const fensterRef = useRef(fenster);
  fensterRef.current = fenster;
  // Was nach dem Fenster weiterläuft (Anmeldung mit dem schon erhaltenen Google-Nachweis oder die Weiterleitung)
  const wartet = useRef<(() => Promise<void>) | null>(null);
  const [zeigen, setZeigen] = useState(false);
  const mitFenster = (weiter: () => Promise<void>): Promise<void> => {
    if (fensterRef.current?.offen) { wartet.current = weiter; setFehler(null); setZeigen(true); return Promise.resolve(); }
    return weiter();
  };
  const mitFensterRef = useRef(mitFenster);
  mitFensterRef.current = mitFenster;
  const fensterWeiter = () => { const w = wartet.current; wartet.current = null; setZeigen(false); void w?.(); };
  const fensterZu = () => { wartet.current = null; setZeigen(false); };
  useEffect(() => {
    let alive = true;
    if (!api.authProviders || !api.signInWithGoogle) return;
    providersLaden().then((p) => alive && setGoogle(p.google)).catch(() => undefined);
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const clientId = googleClientId();
    if (!google || gsi === "aus" || !clientId || !knopf.current) return;
    let alive = true;
    void (async () => {
      try {
        const [id, nonce] = await Promise.all([gsiLaden(), nonceErzeugen()]);
        if (!alive || !knopf.current) return;
        id.initialize({
          client_id: clientId, nonce: nonce.hash, ux_mode: "popup", use_fedcm_for_prompt: true,
          callback: async (r: { credential?: string }) => {
            const credential = r.credential;
            if (!credential) return;
            await mitFensterRef.current(async () => {
              const f = vorherRef.current?.() ?? null;
              if (f) { setFehler(f); return; }
              setFehler(null);
              setBusy(true);
              try {
                await api.signInWithGoogleIdToken!(credential, nonce.roh);
                const path = sichererPfad(redirectPath, "/");
                window.location.assign(path);
              } catch {
                setFehler(t("Die Anmeldung mit Google hat nicht geklappt. Bitte gleich noch einmal versuchen.", "Signing in with Google did not work. Please try again shortly."));
                setBusy(false);
              }
            });
          },
        });
        const dunkel = document.documentElement.dataset.theme === "dark" || (!document.documentElement.dataset.theme && matchMedia("(prefers-color-scheme: dark)").matches);
        id.renderButton(knopf.current, {
          type: "standard", theme: dunkel ? "filled_black" : "outline", size: "large", shape: "pill", text: "continue_with",
          logo_alignment: "center", locale: currentLang() === "en" ? "en" : "de", width: Math.min(400, Math.max(240, knopf.current.offsetWidth || 320)),
        });
        setGsi("bereit");
      } catch {
        if (alive) setGsi("aus");
      }
    })();
    return () => { alive = false; };
  }, [google, gsi === "aus", redirectPath]);
  if (!google) return null;
  const los = () => mitFenster(async () => {
    const f = vorherRef.current?.() ?? null;
    if (f) { setFehler(f); return; }
    setFehler(null);
    setBusy(true);
    try {
      await api.signInWithGoogle!(redirectPath);
    } catch {
      setFehler(t("Die Anmeldung mit Google ließ sich nicht starten. Bitte gleich noch einmal versuchen.", "Signing in with Google could not be started. Please try again shortly."));
      setBusy(false);
    }
  });
  const fensterElement = fenster && (
    <Dialog open={zeigen} onClose={fensterZu} title={fenster.titel}>
      {fenster.inhalt}
      <div className="row">
        <button type="button" className="primary" onClick={fensterWeiter} disabled={!fenster.bereit}>{t("Weiter", "Continue")}</button>
        <button type="button" onClick={fensterZu}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
  if (gsi !== "aus") {
    return (
      <div className="tk-google stack">
        <div ref={knopf} className="tk-google-gsi" aria-busy={busy || gsi === "laedt"} />
        {fehler && <small className="tk-error" role="alert">{fehler}</small>}
        {fensterElement}
        <div className="tk-oder" role="separator" aria-label={t("oder", "or")}><span>{t("oder", "or")}</span></div>
      </div>
    );
  }
  return (
    <div className="tk-google stack">
      <button type="button" className="btn tk-google-knopf" onClick={() => void los()} disabled={busy} aria-busy={busy}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
        </svg>
        <span>{busy ? t("Weiter zu Google …", "Redirecting to Google …") : t("Mit Google fortfahren", "Continue with Google")}</span>
      </button>
      {fehler && <small className="tk-error" role="alert">{fehler}</small>}
      {fensterElement}
      <div className="tk-oder" role="separator" aria-label={t("oder", "or")}><span>{t("oder", "or")}</span></div>
    </div>
  );
}
